import { useCallback, useEffect, useRef } from 'react';

const PLAYED_COLOR = '#D53F8C';
const UNPLAYED_COLOR = 'rgba(255, 255, 255, 0.15)';
const BAR_GAP = 1;
const MIN_BAR_HEIGHT_RATIO = 0.05;

interface MidiDensityCanvasProps {
  density: number[];
  progress: number;
  onSeek?: (progress: number) => void;
}

function draw(canvas: HTMLCanvasElement, density: number[], progress: number) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  /* eslint-disable no-param-reassign */
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  /* eslint-enable no-param-reassign */
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, width, height);

  const barCount = density.length;
  const totalGap = BAR_GAP * (barCount - 1);
  const barWidth = Math.max((width - totalGap) / barCount, 1);
  const progressX = progress * width;

  for (let i = 0; i < barCount; i += 1) {
    const x = i * (barWidth + BAR_GAP);
    const normalised = Math.max(density[i], MIN_BAR_HEIGHT_RATIO);
    const barHeight = normalised * height;
    const y = (height - barHeight) / 2;

    ctx.fillStyle = x + barWidth <= progressX ? PLAYED_COLOR : UNPLAYED_COLOR;
    ctx.beginPath();
    if ('roundRect' in ctx) {
      (ctx as any).roundRect(x, y, barWidth, barHeight, 1);
    } else {
      ctx.rect(x, y, barWidth, barHeight);
    }
    ctx.fill();
  }
}

export const MidiDensityCanvas = ({
  density,
  progress,
  onSeek,
}: MidiDensityCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && density.length > 0) {
      draw(canvasRef.current, density, progress);
    }
  }, [density, progress]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!onSeek || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const clickProgress = (e.clientX - rect.left) / rect.width;
      onSeek(Math.max(0, Math.min(1, clickProgress)));
    },
    [onSeek]
  );

  if (density.length === 0) return null;

  return (
    <canvas
      ref={canvasRef}
      onClick={handleClick}
      style={{
        width: '100%',
        height: '40px',
        cursor: 'pointer',
        borderRadius: '4px',
        display: 'block',
      }}
    />
  );
};
